import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeUsers } from '../../data/listeners/users';
import { subscribeTenants } from '../../data/listeners/tenants';
import { adminCreateUser, adminUpdateUser, adminSetUserActive, adminDeleteUser } from '../../data/commands/userCommands';
import { ROLES } from '../../context/AuthContext';
import { useTerritories } from '../../context/TerritoriesContext';
import Icon from '../../components/Icon';

const ROLE_LABEL = { hqadmin: 'HQ Admin', owner: 'Owner', manager: 'Manager', agent: 'Agent' };
const SCOPED = ['agent', 'manager']; // roles that carry a territory scope

// Add OR edit a user. In edit mode the email/password are fixed (identity is
// immutable here); name/role/territories/manager are editable. Users are
// Admin-SDK-only, so both paths ride gated callables.
function UserModal({
  mode, user, managers, tenants = [], onClose,
}) {
  const isEdit = mode === 'edit';
  const { role: callerRole, activeTenantId } = useAuth();
  const { territories } = useTerritories();
  // Owners build teams only (manager/agent); only HQ Admin can create owners/HQ.
  const allowedRoles = callerRole === 'hqadmin' ? ROLES : ['manager', 'agent'];
  // HQ Admin can place a new user in any tenant (Phase 4); others use their own.
  const showTenant = callerRole === 'hqadmin' && !isEdit;
  const [form, setForm] = useState(() => ({
    name: user?.name ?? '',
    email: user?.email ?? '',
    password: 'Presto-2026!',
    role: user?.role && allowedRoles.includes(user.role) ? user.role : 'agent',
    territoryIds: user?.territoryIds ?? [],
    reportsTo: user?.reportsTo ?? '',
    tenantId: user?.tenantId ?? activeTenantId ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleTerr = (id) => setForm((f) => ({
    ...f,
    territoryIds: f.territoryIds.includes(id)
      ? f.territoryIds.filter((t) => t !== id)
      : [...f.territoryIds, id],
  }));
  const scoped = SCOPED.includes(form.role);

  async function save() {
    setError(''); setBusy(true);
    try {
      const territoryIds = scoped ? form.territoryIds : [];
      const reportsTo = form.role === 'agent' ? (form.reportsTo || null) : null;
      if (isEdit) {
        await adminUpdateUser({
          uid: user.uid, name: form.name, role: form.role, territoryIds, reportsTo,
        });
      } else {
        await adminCreateUser({
          name: form.name, email: form.email, password: form.password, role: form.role, territoryIds, reportsTo, tenantId: form.tenantId,
        });
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save the user.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header><Icon name="users" size={18} /> {isEdit ? 'Edit user' : 'Add user'}</header>
        <div className="body">
          {showTenant && (
            <div className="field"><label>Tenant</label>
              <select className="input" aria-label="Tenant" value={form.tenantId} onChange={set('tenantId')}>
                {tenants.length === 0 && <option value={form.tenantId}>{form.tenantId || '—'}</option>}
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name ?? t.id}</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Full name</label><input className="input" value={form.name} onChange={set('name')} placeholder="Juan dela Cruz" /></div>
          <div className="field"><label>Email</label><input className="input" type="email" value={form.email} onChange={set('email')} placeholder="juan@presto.ph" disabled={isEdit} /></div>
          {!isEdit && (
            <div className="field"><label>Temporary password</label>
              {/* BUG-023: an admin sets a temp password FOR SOMEONE ELSE, so tell
                  password managers to leave this field alone. Their injected
                  icons/overlays sit inside this React-managed container and can
                  crash reconciliation when you type (the cross-platform "crash"
                  testers hit on iPad + Windows). autoComplete=off + the per-vendor
                  ignore hints stop the injection. */}
              <input
                className="input"
                type="text"
                autoComplete="off"
                value={form.password}
                onChange={set('password')}
              />
            </div>
          )}
          <div className="field"><label>Role</label><select className="input" value={form.role} onChange={set('role')}>{allowedRoles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select></div>
          {scoped && (
            <div className="field">
              <label>Territories</label>
              <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
                {territories.length === 0 && <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>No territories yet — add them in Territory Settings.</span>}
                {territories.map((t) => (
                  <label key={t.id} className="check" style={{ padding: 0 }}>
                    <input type="checkbox" checked={form.territoryIds.includes(t.id)} onChange={() => toggleTerr(t.id)} /> {t.label ?? t.id}
                  </label>
                ))}
              </div>
            </div>
          )}
          {form.role === 'agent' && (
            <div className="field"><label>Reports to (manager)</label>
              <select className="input" value={form.reportsTo ?? ''} onChange={set('reportsTo')}>
                <option value="">— none —</option>
                {managers.map((m) => <option key={m.uid} value={m.uid}>{m.name}</option>)}
              </select>
            </div>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create user')}</button>
        </footer>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const { tenantId, authUser, role: callerRole } = useAuth();
  const { terrLabel } = useTerritories();
  // Owners can't manage owner/HQ accounts (only their own teams).
  const canManage = (u) => callerRole === 'hqadmin' || (u.role !== 'owner' && u.role !== 'hqadmin');
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]); // HQ-only tenant picker for Add user (Phase 4)
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', user? }
  // Two-step permanent delete (2026-07-21): first click arms THIS row only.
  const [confirmDeleteUid, setConfirmDeleteUid] = useState(null);

  useEffect(() => {
    setError('');
    return subscribeUsers({ tenantId }, setUsers, (e) => setError(e.message || 'Could not load users.'));
  }, [tenantId]);

  useEffect(() => {
    if (callerRole !== 'hqadmin') { setTenants([]); return undefined; }
    return subscribeTenants(setTenants, () => setTenants([]));
  }, [callerRole]);

  const managers = useMemo(() => users.filter((u) => u.role === 'manager'), [users]);
  const nameByUid = useMemo(() => Object.fromEntries(users.map((u) => [u.uid, u.name])), [users]);

  async function toggleActive(u) {
    setError('');
    try {
      await adminSetUserActive(u.uid, !u.active);
    } catch (err) {
      setError(err.message || 'Could not update user.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <div className="toolbar">
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'add' })}><Icon name="plus" size={15} /> Add user</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Scope</th><th>Reports to</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.uid}>
                <td style={{ fontWeight: 'var(--font-semibold)' }}>{u.name}</td>
                <td>{u.email}</td>
                <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                <td>
                  {(u.territoryIds ?? [])
                    .map((id) => terrLabel(id))
                    .join(', ') || '—'}
                </td>
                <td className="muted">{u.reportsTo ? (nameByUid[u.reportsTo] ?? '—') : '—'}</td>
                <td>
                  <span className={`badge tier-${u.active ? 'Anchor' : 'Small'}`}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canManage(u) ? (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => setModal({ mode: 'edit', user: u })}>Edit</button>
                      <button className="btn btn-ghost btn-sm" disabled={u.uid === authUser?.uid} onClick={() => toggleActive(u)}>
                        {u.active ? 'Deactivate' : 'Reactivate'}
                      </button>
                      {confirmDeleteUid === u.uid ? (
                        <>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'var(--presto-error)', color: '#fff' }}
                            onClick={async () => {
                              try {
                                await adminDeleteUser(u.uid);
                                setConfirmDeleteUid(null);
                              } catch (e) { setError(e.message || 'Could not delete user.'); }
                            }}
                          >
                            Delete forever
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDeleteUid(null)}>Cancel</button>
                        </>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--presto-error)' }}
                          disabled={u.uid === authUser?.uid}
                          title="Permanently delete this user (audited; their leads become unassigned)"
                          onClick={() => setConfirmDeleteUid(u.uid)}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  ) : <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>—</span>}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {modal && <UserModal mode={modal.mode} user={modal.user} managers={managers} tenants={tenants} onClose={() => setModal(null)} />}
    </div>
  );
}
